import { OPS_PERMISSIONS as P, userHasAnyPermission } from "./permissions";
import { ROLES, normalizeRole } from "./roles";

export const OPS_REPORT_GROUPS = [
  {
    id: "executive",
    label: "Commercial Executive",
    audience: "executive",
    permissions: [P.INSTITUTIONS_VIEW, P.ASSIGNMENT_MANAGE],
    reports: [
      {
        slug: "call-centre-manager-performance",
        name: "Call Centre Manager Performance",
        purpose:
          "Each case assigned to an agent under a call centre manager, with the uploaded portfolio figures and the amount recovered.",
        filters: ["manager", "provider", "portfolio_group", "search"],
      },
      {
        slug: "call-centre-agent-performance",
        name: "Call Centre Agent Performance",
        purpose:
          "Each assigned case, without the manager column. Uploaded figures stay on the portfolio snapshot; recovery is what has been collected on the case.",
        filters: ["provider", "portfolio_group", "search"],
      },
      {
        slug: "recovery-targets-by-manager",
        name: "Manager Recovery Targets",
        purpose: "How each call centre manager's agents are doing against the recovery targets set on their assignment batches.",
        filters: [],
      },
      {
        slug: "recovery-targets-by-agent",
        name: "Agent Recovery Targets",
        purpose: "Each assignment batch, with the target, amount recovered in the window, and the performance rating.",
        filters: [],
      },
      {
        slug: "sms-message-log",
        name: "SMS Message Log",
        purpose: "Every text sent for reminders, portfolio uploads, payments, and other notices across the institutions you can see.",
        filters: ["search"],
      },
      {
        slug: "commercial-operations-overview",
        name: "Commercial Operations Overview",
        purpose: "Org-wide activity across institutions, cases, and recoveries.",
        filters: ["date", "period"],
      },
      {
        slug: "institution-portfolio-health",
        name: "Institution Portfolio Health",
        purpose: "Ageing and recovery posture by institution.",
        filters: ["date", "search"],
      },
      {
        slug: "institution-assignment-audit",
        name: "Institution Assignment Audit",
        purpose: "Who is scoped to which institution and when.",
        filters: ["date", "search"],
      },
    ],
  },
  {
    id: "manager",
    label: "Call Centre Manager",
    audience: "manager",
    permissions: [P.ASSIGNMENT_MANAGE, P.CONTACT_CENTRE_MANAGE, P.CASES_ASSIGN],
    reports: [
      {
        slug: "call-centre-agent-performance",
        name: "Call Centre Agent Performance",
        purpose:
          "Performance of the agents on your providers. One row per assigned case, using the uploaded portfolio figures and collections on the case.",
        filters: ["provider", "portfolio_group", "search"],
      },
      {
        slug: "agent-performance",
        name: "Agent Performance Report",
        purpose: "Measures individual agent productivity and effectiveness.",
        filters: ["date", "agent", "period"],
      },
      {
        slug: "case-assignment-distribution",
        name: "Case Assignment & Distribution Report",
        purpose: "Shows how workloads are distributed among agents.",
        filters: ["date", "agent", "case_status", "search"],
      },
      {
        slug: "agent-workload",
        name: "Agent Workload Report",
        purpose: "Identifies over/under-utilised agents.",
        filters: ["date", "agent"],
      },
      {
        slug: "contact-centre-activity",
        name: "Contact Centre Activity Report",
        purpose: "Tracks total contact-centre operations.",
        filters: ["date", "agent"],
      },
      {
        slug: "recovery-performance-by-agent",
        name: "Recovery Performance by Agent Report",
        purpose: "Compares financial performance among agents.",
        filters: ["date", "agent"],
      },
      {
        slug: "team-recovery-targets",
        name: "Team Recovery Targets",
        purpose: "Assignment batches for your agents, scored against the recovery target set when the cases were assigned.",
        filters: [],
      },
      {
        slug: "sms-message-log",
        name: "SMS Message Log",
        purpose: "Texts sent for your institutions: repayment reminders, uploads, payments, and other notices.",
        filters: ["search"],
      },
      {
        slug: "promise-to-pay-monitoring",
        name: "Promise-to-Pay Monitoring Report",
        purpose: "Monitors commitments obtained by agents.",
        filters: ["date", "agent", "search"],
      },
      {
        slug: "escalated-cases",
        name: "Escalated Cases Report",
        purpose: "Manages serious/problem cases escalated by agents.",
        filters: ["date", "agent", "escalation_status", "search"],
      },
      {
        slug: "payment-initiation-success",
        name: "Payment Initiation & Success Report",
        purpose: "Monitors payment requests initiated by agents.",
        filters: ["date", "agent", "payment_status", "payment_channel", "search"],
      },
      {
        slug: "sla-follow-up-compliance",
        name: "SLA & Follow-Up Compliance Report",
        purpose: "Managers and agents who missed the provider SLA window.",
        filters: ["date", "agent", "case_status", "search"],
      },
      {
        slug: "team-kpi-dashboard",
        name: "Team KPI Dashboard Report",
        purpose: "Manager assignment and agent engagement against provider SLA days.",
        filters: ["date", "period"],
      },
    ],
  },
  {
    id: "agent",
    label: "Call Centre Agent",
    audience: "agent",
    permissions: [P.CASES_VIEW_ASSIGNED, P.CASES_LOG_INTERACTION],
    reports: [
      {
        slug: "my-assigned-cases",
        name: "My Assigned Cases Report",
        purpose: "Agent's principal working queue.",
        filters: ["date", "case_status", "search"],
      },
      {
        slug: "my-daily-worklist",
        name: "My Daily Worklist Report",
        purpose: "Shows cases requiring action today.",
        filters: ["date", "case_status", "search"],
      },
      {
        slug: "my-contact-history",
        name: "My Contact/Interaction History Report",
        purpose: "Tracks all debtor engagements.",
        filters: ["date", "search"],
      },
      {
        slug: "my-payment-initiation",
        name: "My Payment Initiation Report",
        purpose: "Tracks payment requests initiated by the agent.",
        filters: ["date", "payment_status", "payment_channel", "search"],
      },
      {
        slug: "my-recovery-performance",
        name: "My Recovery Performance Report",
        purpose: "Measures personal recovery achievement.",
        filters: ["date", "period"],
      },
      {
        slug: "my-recovery-targets",
        name: "My Recovery Targets",
        purpose: "Each batch assigned to you, and whether the recovered amount met the target for that window.",
        filters: [],
      },
      {
        slug: "sms-message-log",
        name: "My SMS Log",
        purpose: "Texts sent about the customers and cases assigned to you.",
        filters: ["search"],
      },
      {
        slug: "my-promise-to-pay",
        name: "My Promise-to-Pay Report",
        purpose: "Tracks commitments secured by agent.",
        filters: ["date", "search"],
      },
      {
        slug: "my-follow-up-reminder",
        name: "My Follow-Up & Reminder Report",
        purpose: "Manages upcoming customer follow-ups.",
        filters: ["date", "search"],
      },
      {
        slug: "my-escalated-cases",
        name: "My Escalated Cases Report",
        purpose: "Tracks cases submitted to supervisor.",
        filters: ["date", "escalation_status", "search"],
      },
      {
        slug: "my-closed-cases",
        name: "My Closed Cases Report",
        purpose: "Shows successfully resolved/recovered cases.",
        filters: ["date", "search"],
      },
      {
        slug: "my-productivity-kpi",
        name: "My Productivity/KPI Report",
        purpose: "Provides personal performance scorecard.",
        filters: ["date", "period"],
      },
    ],
  },
  {
    id: "admin",
    label: "System Administrator",
    audience: "admin",
    permissions: [P.OPS_USERS_MANAGE],
    reports: [
      {
        slug: "call-centre-manager-performance",
        name: "Call Centre Manager Performance",
        purpose:
          "Each case assigned to an agent under a call centre manager, with the uploaded portfolio figures and the amount recovered.",
        filters: ["manager", "provider", "portfolio_group", "search"],
      },
      {
        slug: "call-centre-agent-performance",
        name: "Call Centre Agent Performance",
        purpose:
          "Each assigned case, without the manager column. Uploaded figures stay on the portfolio snapshot; recovery is what has been collected on the case.",
        filters: ["provider", "portfolio_group", "search"],
      },
      {
        slug: "recovery-targets-by-manager",
        name: "Manager Recovery Targets",
        purpose: "How each call centre manager's agents are doing against the recovery targets set on their assignment batches.",
        filters: [],
      },
      {
        slug: "recovery-targets-by-agent",
        name: "Agent Recovery Targets",
        purpose: "Each assignment batch, with the target, amount recovered in the window, and the performance rating.",
        filters: [],
      },
      {
        slug: "sms-message-log",
        name: "SMS Message Log",
        purpose: "Every text sent on the platform, tagged as a reminder, upload, payment, or other notice.",
        filters: ["search"],
      },
      {
        slug: "manager-sla-scorecard",
        name: "Call Centre Manager SLA Scorecard",
        purpose: "Each uploaded portfolio case, the manager who assigned it, and whether that assignment met the provider SLA.",
        filters: ["date", "manager", "search"],
      },
      {
        slug: "agent-sla-scorecard",
        name: "Agent SLA Scorecard",
        purpose: "Each assigned case, the agent, the manager who assigned it, and whether engagement met the provider SLA.",
        filters: ["date", "agent", "search"],
      },
      {
        slug: "user-account",
        name: "User Account Report",
        purpose: "Shows all application users.",
        filters: ["date", "search"],
      },
      {
        slug: "roles-privileges",
        name: "Roles & Privileges Report",
        purpose: "Reviews access rights.",
        filters: ["date", "search"],
      },
      {
        slug: "user-login-access",
        name: "User Login & Access Report",
        purpose: "Monitors access activity.",
        filters: ["date", "search"],
      },
      {
        slug: "system-audit-trail",
        name: "System Audit Trail Report",
        purpose: "Provides immutable history of system activities.",
        filters: ["date", "search"],
      },
      {
        slug: "portfolio-upload-audit",
        name: "Portfolio Upload Audit Report",
        purpose: "Tracks all uploaded debtor files.",
        filters: ["date", "search"],
      },
      {
        slug: "system-transaction",
        name: "System Transaction Report",
        purpose: "System-wide payment transaction monitoring.",
        filters: ["date", "payment_status", "payment_channel", "search"],
      },
      {
        slug: "system-api-integration",
        name: "System/API Integration Report",
        purpose: "Monitors external integrations.",
        filters: ["date", "search"],
      },
      {
        slug: "security-exception",
        name: "Security & Exception Report",
        purpose: "Detects suspicious system activities.",
        filters: ["date", "search"],
      },
      {
        slug: "system-performance-availability",
        name: "System Performance & Availability Report",
        purpose: "Measures technical health.",
        filters: ["date", "period"],
      },
      {
        slug: "master-system-operations",
        name: "Master System Operations Report",
        purpose: "Consolidated administrator view of platform activity.",
        filters: ["date", "period"],
      },
    ],
  },
];

export const OPS_REPORTS = OPS_REPORT_GROUPS.flatMap((group) =>
  group.reports.map((report) => ({ ...report, audience: group.audience, groupLabel: group.label }))
);

export function getOpsReport(slug, user) {
  const matches = OPS_REPORTS.filter((report) => report.slug === slug);
  if (!matches.length) return null;
  if (!user) return matches[0];
  return matches.find((report) => canSeeReportAudience(user, report.audience)) || null;
}

export function reportPath(slug) {
  return `/dashboard/reports/${slug}`;
}

export function canSeeReportAudience(user, audience) {
  const role = normalizeRole(user?.role);
  if (role === ROLES.SUPER_ADMIN) return audience === "admin";
  if (role === ROLES.EXECUTIVE_MANAGER) return audience === "executive";
  if (audience === "manager") return role === ROLES.CALL_CENTER_MANAGER;
  if (audience === "agent") return role === ROLES.CALL_CENTER_AGENT;
  return false;
}

export function visibleReportGroups(user) {
  return OPS_REPORT_GROUPS.filter((group) => canSeeReportAudience(user, group.audience)).map((group) => ({
    ...group,
    reports: group.reports,
  }));
}

export function getReportNavChildren(user) {
  return visibleReportGroups(user).map((group) => ({
    label: group.label,
    children: group.reports.map((report) => ({
      label: report.name,
      description: report.purpose,
      path: reportPath(report.slug),
    })),
  }));
}

export const REPORT_NAV_CHILDREN = OPS_REPORT_GROUPS.map((group) => ({
  label: group.label,
  audience: group.audience,
  permissions: group.permissions,
  children: group.reports.map((report) => ({
    label: report.name,
    description: report.purpose,
    path: reportPath(report.slug),
    audience: group.audience,
    permissions: group.permissions,
  })),
}));
